import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { CustomerStat, DormantCustomer } from "@/services/wholesale-analytics";
import { fmtKm, fmtUnixDate } from "@/lib/wholesale-format";
import { filterCustomers, nextSort, sortCustomers, type CustomerSortKey, type SortState } from "@/lib/wholesale-analytics-table";
import { Section, TH, THEAD_ROW, Delta, ShareBar, ShowAll, SortTh, TableSearch, fmtInt } from "./parts";

const TOP = 10;

function PartnerLink({ id, name }: { id: string; name: string }) {
  return (
    <Link to="/wholesale/partners/$partnerId" params={{ partnerId: id }} className="hover:underline underline-offset-2" title={name}>
      {name}
    </Link>
  );
}

/** `compare` je lažno kad prethodni period nema narudžbi — tada kolona promjene samo ponavlja „novo". */
export function CustomersSection({ customers, compare }: { customers: CustomerStat[]; compare: boolean }) {
  const [sort, setSort] = useState<SortState<CustomerSortKey>>({ key: "revenue", dir: "desc" });
  const [query, setQuery] = useState("");
  const [all, setAll] = useState(false);
  const sorted = useMemo(() => sortCustomers(filterCustomers(customers, query), sort), [customers, query, sort]);
  // Pretraga već sužava listu, pa se pogoci ne skraćuju na TOP.
  const rows = all || query.trim() ? sorted : sorted.slice(0, TOP);
  // Traka udjela ostaje skalirana prema najvećem kupcu perioda, bez obzira na sort i pretragu.
  const maxShare = customers.reduce((m, c) => Math.max(m, c.share), 0);
  const onSort = (k: CustomerSortKey) => setSort((s) => nextSort(s, k));
  return (
    <Section
      title="Kupci"
      hint="Ko je najviše kupio u ovom periodu, po prometu. Klik na naslov kolone sortira."
      action={customers.length > 0 && <TableSearch value={query} onChange={setQuery} placeholder="Traži kupca" />}
    >
      {customers.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">Nijedan kupac nije naručio u ovom periodu.</p>
      ) : sorted.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">Nijedan kupac ne odgovara pretrazi „{query.trim()}".</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={THEAD_ROW}>
                  <SortTh k="name" sort={sort} onSort={onSort} align="left" className="pl-3">Kupac</SortTh>
                  <SortTh k="revenue" sort={sort} onSort={onSort}>Promet</SortTh>
                  {compare && <SortTh k="delta" sort={sort} onSort={onSort} className="hidden sm:table-cell">Promjena</SortTh>}
                  <th className={`${TH} text-left w-36 hidden md:table-cell`}>Udio</th>
                  <SortTh k="orders" sort={sort} onSort={onSort}>Nar.</SortTh>
                  <SortTh k="units" sort={sort} onSort={onSort} className="hidden sm:table-cell">Kom.</SortTh>
                  <SortTh k="avgOrder" sort={sort} onSort={onSort} className="hidden lg:table-cell">Prosj. nar.</SortTh>
                  <SortTh k="lastOrderAt" sort={sort} onSort={onSort} className="pr-3 hidden sm:table-cell">Zadnja</SortTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.partnerId} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-2 pl-3 font-medium max-w-[12rem] lg:max-w-[20rem] truncate"><PartnerLink id={c.partnerId} name={c.name} /></td>
                    <td className="py-2.5 px-2 text-right tabular-nums font-medium whitespace-nowrap">{fmtKm(c.revenue)}</td>
                    {compare && (
                      <td className="py-2.5 px-2 text-right hidden sm:table-cell"><Delta current={c.revenue} previous={c.prevRevenue} /></td>
                    )}
                    <td className="py-2.5 px-2 hidden md:table-cell"><ShareBar value={c.share} max={maxShare} /></td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{c.orders}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums hidden sm:table-cell">{fmtInt(c.units)}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums hidden lg:table-cell whitespace-nowrap text-muted-foreground">{fmtKm(c.avgOrder)}</td>
                    <td className="py-2.5 px-2 pr-3 text-right tabular-nums text-muted-foreground hidden sm:table-cell">{fmtUnixDate(c.lastOrderAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!query.trim() && sorted.length > TOP && <ShowAll open={all} total={sorted.length} onToggle={() => setAll(!all)} />}
        </>
      )}
    </Section>
  );
}

/** Kupci koji su naručivali u prethodnom periodu, a u ovom nisu: lista za poziv. Stranica je
 *  prikazuje samo kad ima nekoga u njoj. */
export function DormantSection({ dormant }: { dormant: DormantCustomer[] }) {
  const [all, setAll] = useState(false);
  const rows = all ? dormant : dormant.slice(0, TOP);
  return (
    <Section title="Nisu naručili" hint="Kupovali su u prethodnom periodu, a u ovom nisu. Promet i datum su iz prethodnog perioda.">
      {/* Mreža kartica umjesto liste: sekcija ide preko cijele širine, a lista bi ostavila
          prazan desni dio. `gap-px` na pozadini crta razdjelnike i kad se red ne popuni. */}
      <ul className="grid gap-px bg-foreground/10 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((d) => (
          <li key={d.partnerId} className="bg-background px-3 py-2.5 text-xs flex items-baseline gap-3">
            <span className="font-medium flex-1 min-w-0 truncate"><PartnerLink id={d.partnerId} name={d.name} /></span>
            <span className="text-right shrink-0">
              <span className="block tabular-nums">{fmtKm(d.prevRevenue)}</span>
              <span className="block text-[10px] text-muted-foreground tabular-nums">zadnja {fmtUnixDate(d.lastOrderAt)}</span>
            </span>
          </li>
        ))}
        {/* Prazne ćelije do kraja reda, da zadnji red ne ostavi sivu rupu. */}
        {Array.from({ length: (3 - (rows.length % 3)) % 3 }).map((_, i) => (
          <li key={`pad-${i}`} aria-hidden className="bg-background hidden lg:block" />
        ))}
      </ul>
      {dormant.length > TOP && <ShowAll open={all} total={dormant.length} onToggle={() => setAll(!all)} />}
    </Section>
  );
}
